import { useEffect, useState, useRef } from "react";
import readMessageStream from "../../functions/requests/readMessageStream";
import IChatMessage from "../../types/interfaces/IChatMessage";
import { MessageRole } from "../../types/enums/MessageRole";
import ChatMessage from "./ChatMessage";
import { Box } from "@mui/material";
import IModel from "../../types/interfaces/IModel";

export type IncomingMessageStream = ReadableStream<Uint8Array>;

export type IncomingMessageProps = {
  stream?: IncomingMessageStream;
  scrollCallback: () => void;
  model: IModel | null;
};

export default function IncomingMessage({
  stream,
  scrollCallback,
  model,
}: IncomingMessageProps) {
  const [content, setContent] = useState("");
  // Track when we should immediately clear content on stream changes
  const didStreamExist = useRef(false);

  useEffect(() => {
    // If the stream is removed/set to undefined and we had a stream before,
    // immediately clear the content to prevent duplicate display
    if (stream === undefined && didStreamExist.current) {
      setContent("");
      didStreamExist.current = false;
      return;
    }
    
    // Skip processing if no stream or if it's locked
    if (stream === undefined) return;
    if (stream.locked) return;
    
    // Mark that we have a valid stream
    didStreamExist.current = true;
    
    // Reset content when a new stream starts
    setContent("");
    
    // Process the stream
    readMessageStream(stream, (token) => {
      setContent((prev) => prev + token);
      scrollCallback();
    })
      .then()
      .catch((_) => {});
      
    // Clean up function to clear content when component unmounts or stream changes
    return () => {
      if (didStreamExist.current) {
        setContent("");
      }
    };
  }, [stream, scrollCallback]);

  // Don't render anything if there's no content (prevents flash of empty message)
  if (!content && stream === undefined) {
    return null;
  }

  const chatMessage: IChatMessage = {
    id: "",
    conversationId: "",
    role: MessageRole.Assistant,
    content: content,
    updated: new Date(),
    model: model,
    hasFeedback: false,
  };
  
  return (
    <Box sx={{ width: "100%" }}>
      <ChatMessage fromUser={false} message={chatMessage} streaming={true}/>
    </Box>
  );
}
